/**
*  Dropdown
* @author 
* 
* @copyright Klipfolio, All rights reserved
*/
import React, {useEffect, useState, useRef} from 'react'
import PropTypes from 'prop-types'
import styled, {css} from 'styled-components'
import Icon from '@icons'
import Link from "@atoms/link"
import { useComponentVisible } from '@particles/hooks'


const propTypes = {
  title: PropTypes.string.isRequired,
  init: PropTypes.number,
  options: PropTypes.array,
  links: PropTypes.bool,
  suffix: PropTypes.string,
  multiple: PropTypes.bool,
  icon: PropTypes.node,
  items: PropTypes.array,
  refine: PropTypes.func,
  currentRefinement: PropTypes.array,
  change: PropTypes.func,
  initAlgolia: PropTypes.string,
  defaultClick: PropTypes.number

}

const StyledDropdownContainer = styled.div`
  position: relative;
`

const ClearRefinements = styled.div`
  display: grid;
  place-items: center;
  width: 1rem;
  height: 1rem;
  border-radius: 50%;
  background: #F6F8FE;
  margin-left: 0.5rem;
  transition: all ease 150ms;
  visibility: ${props => props.show ? "visible" : "hidden"};

  ${Icon} {
    filter: opacity(80%);
  }

  :hover {
    background: #D7DCEA !important;
    ${Icon} {
      filter: opacity(100%) brightness(60%);
    }
  }

`

const StyledDropdownButton = styled.button`
  font-size: 0.75rem;
  background: #FFFFFF;
  cursor: pointer;
  border: 1px solid #D7DCEA;
  box-sizing: border-box;
  border-radius: 2rem;
  padding: 0.75rem 0.75rem 0.75rem ${props => props.hasIcon ? 0.75 : 1}rem;
  color: ${({isSelected, hasIcon}) => isSelected && hasIcon ? "#34AD78" : "#485066"};
  line-height: 1rem;
  display: flex;
  align-items: center;
  min-width: ${({hasIcon}) => hasIcon ? "auto" : "206px"};
  height: 40px;
  font-weight: 500;

  span {
    ${({hasIcon}) => hasIcon ? "margin-left: 0.5rem" : ""};
  }

  transition: all ease 150ms;

  ${props => props.isOpen && !props.hasIcon && css`
    box-shadow: 0px 0px 40px rgba(0, 0, 0, 0.1);

    ${Icon} {
      transform: rotate(180deg);
    }
  `}

  :hover {
    background: #F6F8FE;
    ${ClearRefinements} {
      background: white;
    }
    ${props => !props.hasIcon && css`
      box-shadow: 0px 0px 40px rgba(0, 0, 0, 0.1);
    `}
  }

`

const IconContainer = styled.div`
  display: flex;
  position: relative;
`

const CountIcon = styled.div`
  display: grid;
  place-items: center;
  text-align: center;
  font-weight: bold;
  line-height: 0.5rem;
  font-size: 0.5rem;
  top: -0.2rem;
  right: -0.2rem;
  width: 1rem;
  height: 1rem;
  border-radius: 50%;
  color: #014C33;
  background: #75E1B2;
  ${Icon} {
    filter: invert(20%) sepia(22%) saturate(4195%) hue-rotate(132deg) brightness(93%) contrast(99%) !important;
  }
`

const DropdownListContainer = styled.div`
  position: absolute;
  top: calc(100% + 0.5rem);
  overflow: hidden;
  box-shadow: 0px 0px 40px rgba(0, 0, 0, 0.1);
  border-radius: 16px;
  border: 1px solid #D7DCEA;
  display: ${props => props.isOpen ? 'flex' : 'none'};
  left: ${props => props.offsetLeft ? props.offsetLeft + "px" : '0'};


`

const StyledDropdownList = styled.ul`
  background: #FFFFFF;
  box-sizing: border-box;
  list-style: none;
  max-height: 380px;
  flex-direction: column;
  display: flex;
  min-width: 206px;
  overflow-y: auto;
  padding: 0.5rem 0;

`

const Li = styled.li`
  
  font-size: 0.75rem;
  color: #000;
  padding: 0.5rem 1.5rem;
  line-height: 1.5rem;
  cursor: pointer;
  transition: all ease 100ms;
  white-space: nowrap;
  display: flex;
  margin: 0;
  

  ${Link} {
    color: #000;
    font-size: 0.75rem;
    line-height: 1.5rem;
    flex: 1;
    display:flex;
    align-items: center;
  }
  

  :hover {
    background: #F6F8FE;
  }
`

const LiSingle = styled(Li)`
  ${({selected}) => selected && css`
    background: #F6F8FE;
    color: #34AD78;
    ${Link} {
      color: #34AD78;
    }
  `}
`

const LiMultiple = styled(Li)`
  align-items: center;   
`



const Checkbox = styled.div`
  background: #F6F8FE;
  border: 1px solid #D7DCEA;
  min-width: 1rem;
  min-height: 1rem;
  box-sizing: border-box;
  border-radius: 4px;
  margin-right: 0.5rem;
  position: relative;

  ${({checked}) => checked && css`
    ::after {
      content: "";
      position: absolute;
      width: 7px;
      height: 3px;
      transform: translate(-50%, -50%) rotate(-45deg);
      border-bottom: 1px solid #000;
      border-left: 1px solid #000;
      left: 50%;
      top: 50%;
    }
  `}
`

const Dropdown = ({title, init = -1, options, links, suffix, multiple = false, icon = null, items, refine, currentRefinement, change, initAlgolia, defaultClick = null, ...rest}) => {
  const [selected, setSelected] = useState(multiple ? [] : init)
  const [height, setHeight] = useState(0)
  const [offsetLeft, setOffsetLeft] = useState(0)
  const [showIcon, setShowIcon] = useState(defaultClick === null)
  const listRef = useRef(null)
  const isAlgolia = items ? true : false

  const {
    ref,
    isComponentVisible,
    setIsComponentVisible
  } = useComponentVisible(false)

  useEffect(() => {
    if(refine && initAlgolia) {
      if(change) change(initAlgolia)
      refine([initAlgolia])
      if(multiple) {
        setIsComponentVisible(true)
      }
    } 
    
  }, [initAlgolia, init])


  const handleResize = () => {
    const bounding = listRef.current.getBoundingClientRect()
    if(bounding.bottom > window.innerHeight) {
      setHeight(window.innerHeight - bounding.top - 16)
    } else {
      setHeight(null)
    }
    if(bounding.right > window.innerWidth) {
      setOffsetLeft(window.innerWidth - bounding.right - 32)
    } else {
      setOffsetLeft(0)
    }
  }

  const select = (index) => {
    if(!multiple) {
      if(selected === index) {
        setSelected(-1)
        if(change) change(null)
      } else {
        if(!showIcon && defaultClick !== index) {
          // console.log(defaultClick, index)

          setShowIcon(true)
        }
        setSelected(index)
        if(change) change(options[index])
      }
      setIsComponentVisible(false)
    } else {
      if(selected.includes(index)) {
        if(!showIcon && defaultClick !== selected) {
          setShowIcon(true)
        }
        setSelected(selected.filter(i => i !== index))
      } else {
        setSelected([...selected, index])
      }
    }
  }

  

  useEffect(() => {
    window.addEventListener('resize', handleResize)
    return () => {
      window.removeEventListener('resize', handleResize)
    }
  },[])

  useEffect(() => {
    handleResize()
  },[isComponentVisible])
  const selectedItem = isAlgolia ? items.find((item) => item.isRefined) : options?.[selected];
  const count = multiple ? isAlgolia ? currentRefinement.length : selected.length : icon ? selectedItem?.icon ? true : false : false
  return <StyledDropdownContainer ref={ref} {...rest}>  
    <StyledDropdownButton onClick={() => {setIsComponentVisible(!isComponentVisible)}} hasIcon={icon ? true : false} isSelected={count && multiple} isOpen={isComponentVisible}>
      {icon && <IconContainer>{count && showIcon ? <CountIcon>{selectedItem?.icon ? selectedItem.icon : count > 9 ? "9+" : count}</CountIcon> : icon}</IconContainer>}
      <span>
        {!multiple && (!selectedItem || icon) && title}
        {!multiple && !icon && selectedItem && `${selectedItem.label} ${selectedItem.suffix === false ? "" : suffix || ""}`}
        {multiple && title}
      </span>
      {multiple && <ClearRefinements show={selectedItem} onClick={(e) => {e.stopPropagation(); refine([]); setIsComponentVisible(false)}}><Icon name={"close"} size={0.5}/></ClearRefinements>}
      {!multiple && <div style={{width: "1.5rem"}}></div>}
      <div style={{flex: 1}}></div>
      {icon ? undefined : <Icon name={'chevron-down-line'} size={0.75} margin={"L1"} />}
    </StyledDropdownButton>
    <DropdownListContainer isOpen={isComponentVisible} offsetLeft={offsetLeft} >
      <StyledDropdownList   ref={listRef} height={height} >
        {isAlgolia ? 
          multiple ? 
            items.map((item, index) => (
              <LiMultiple selected={item.isRefined} key={`${item.label}-${index}`} onClick={() => {refine(item.value)}}><Checkbox checked={item.isRefined}/>{item.label}</LiMultiple> 
            ))
            :
            items.map((item, index) => (
              <LiSingle selected={item.isRefined} key={`${item.label}-${index}`} onClick={() => {const val = item.value.pop(); change(val); refine(val ? [val] : []); setIsComponentVisible(false)}}>{item.label}</LiSingle> 
            ))
          :
          options.map((option, index) => {
            if(links) {
              return multiple ? 
                <LiMultiple selected={index === selected} key={`${option.label}-${index}`}><Link  to={option.to}><Checkbox />{option.label}</Link></LiMultiple>
                :  
                <LiSingle selected={index === selected} key={`${option.label}-${index}`} ><Link  to={option.to}>{option.label}</Link></LiSingle>
            } else {
              return multiple ? 
                <LiMultiple selected={index === selected} key={`${option.label}-${index}`} onClick={() => select(index)}><Checkbox checked={selected.includes(index)}/>{option.label}</LiMultiple>
                :
                <LiSingle selected={index === selected} key={`${option.label}-${index}`} onClick={() => select(index)}>{option.label}</LiSingle> 

            } 
          })
        }
      </StyledDropdownList>
    </DropdownListContainer>
  </StyledDropdownContainer>
}



Dropdown.propTypes = propTypes
export default Dropdown